import { Box, Button, Grid, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { categoriesData } from "../../../helpers/Arrays";
import { getCategoryDetail } from "../../../helpers/categoryDetails";
import "./CategoryDetail.css";

const typeAccent = {
  "Engineers & Pioneers": "#23aef4",
  Researchers: "#c11a83",
};

const CategoryDetailTemplate = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const category = getCategoryDetail(pathname);

  if (!category) {
    return <Navigate to="/categories" replace />;
  }

  const accent = typeAccent[category.type] || "#23aef4";
  const otherCategories = categoriesData.filter((item) => item.link !== pathname);

  return (
    <>
      <Box
        component="section"
        className="category-hero"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: "320px", md: "420px" },
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <Box
          className="category-hero__image"
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${category.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(10, 14, 24, 0.35) 0%, rgba(10, 14, 24, 0.88) 100%)",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: "1500px",
            margin: "0 auto",
            px: { xs: "20px", sm: "40px", lg: "80px" },
            py: { xs: "40px", md: "56px" },
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              px: 1.5,
              py: 0.75,
              mb: 2,
              borderRadius: "999px",
              backgroundColor: "rgba(255, 255, 255, 0.14)",
              border: `1px solid ${accent}88`,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: accent,
              }}
            >
              {category.type}
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              color: "#fff",
              fontSize: { xs: "36px", sm: "48px", md: "56px" },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              mb: 1.5,
            }}
          >
            {category.title}
          </Typography>

          <Box
            sx={{
              height: "4px",
              width: { xs: "80px", md: "120px" },
              borderRadius: "999px",
              background: `linear-gradient(90deg, ${accent} 0%, rgba(255,255,255,0.8) 100%)`,
              mb: 2,
            }}
          />
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          width: "100%",
          background:
            "linear-gradient(180deg, #fafbfc 0%, #ffffff 45%, #f7f9fc 100%)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1500px",
            margin: "0 auto",
            px: { xs: "20px", sm: "40px", lg: "80px" },
            py: { xs: "40px", md: "64px" },
          }}
        >
          <Grid container spacing={{ xs: 4, md: 6 }}>
            <Grid item xs={12} lg={7}>
              <Typography
                sx={{
                  fontSize: { xs: "13px", md: "14px" },
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: accent,
                  mb: 1.5,
                }}
              >
                Overview
              </Typography>
              <Typography
                sx={{
                  color: "primary.darker",
                  fontSize: { xs: "16px", md: "17px" },
                  lineHeight: 1.75,
                  mb: 3,
                }}
              >
                {category.brief}
              </Typography>

              {category.downloads?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", md: "14px" },
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: accent,
                      mb: 1.5,
                    }}
                  >
                    Resources
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                    {category.downloads.map((item) => (
                      <Button
                        key={item.label}
                        component="a"
                        href={item.href}
                        download={item.download || undefined}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                        variant="outlined"
                        startIcon={
                          item.external ? (
                            <OpenInNewOutlinedIcon />
                          ) : (
                            <DownloadOutlinedIcon />
                          )
                        }
                        sx={{
                          borderRadius: "999px",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: accent,
                          borderColor: `${accent}55`,
                          backgroundColor: "#fff",
                          "&:hover": {
                            borderColor: accent,
                            backgroundColor: `${accent}10`,
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {category.subCategories && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "18px", md: "20px" },
                      fontWeight: 700,
                      color: "primary.darker",
                      mb: 1.5,
                    }}
                  >
                    {category.subCategories.title}
                  </Typography>
                  <Box
                    component="ul"
                    className="category-sub-list"
                    sx={{ m: 0, pl: 2.5 }}
                  >
                    {category.subCategories.items.map((item) => (
                      <Box component="li" key={item} sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            color: "primary.darker",
                            fontSize: "15px",
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: "20px",
                  backgroundColor: "#fff",
                  border: "1px solid rgba(89, 92, 97, 0.10)",
                  boxShadow: "0 16px 40px rgba(10, 14, 24, 0.08)",
                  position: { lg: "sticky" },
                  top: { lg: "96px" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "20px", md: "22px" },
                    fontWeight: 800,
                    color: "primary.darker",
                    mb: 0.5,
                  }}
                >
                  {category.specsTitle}
                </Typography>
                <Box
                  sx={{
                    height: "3px",
                    width: "60px",
                    borderRadius: "999px",
                    background: `linear-gradient(90deg, ${accent} 0%, ${accent}88 100%)`,
                    mb: 2.5,
                  }}
                />

                {category.specs.map((spec) => (
                  <Box key={spec.label} sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: accent,
                        mb: 0.5,
                      }}
                    >
                      {spec.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: "primary.darker",
                        fontSize: "15px",
                        lineHeight: 1.55,
                      }}
                    >
                      {spec.value}
                    </Typography>
                  </Box>
                ))}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate("/firststage")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    mt: 1,
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "14px",
                    py: 1.2,
                    color: "#fff",
                    background: `linear-gradient(90deg, #23aef4 0%, #c11a83 100%)`,
                    boxShadow: "0 8px 20px rgba(193, 26, 131, 0.22)",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #1e9de0 0%, #a8156e 100%)",
                    },
                  }}
                >
                  Register for this category
                </Button>
              </Box>
            </Grid>
          </Grid>

          {category.gallery?.length > 0 && (
            <Box sx={{ mt: { xs: 5, md: 7 } }}>
              <Typography
                component="h2"
                sx={{
                  textAlign: "center",
                  color: "primary.darker",
                  fontSize: { xs: "24px", md: "32px" },
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  mb: 1,
                }}
              >
                Category{" "}
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Gallery
                </Box>
              </Typography>
              <Box
                sx={{
                  height: "4px",
                  width: "80px",
                  borderRadius: "999px",
                  background:
                    "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
                  margin: "0 auto 32px",
                }}
              />

              <Grid container spacing={2}>
                {category.gallery.map((src, i) => (
                  <Grid item xs={6} sm={4} md={3} key={`${src}-${i}`}>
                    <Box
                      sx={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        border: "1px solid rgba(89, 92, 97, 0.10)",
                        boxShadow: "0 8px 24px rgba(10, 14, 24, 0.08)",
                        aspectRatio: "4/3",
                        "&:hover img": { transform: "scale(1.05)" },
                      }}
                    >
                      <Box
                        component="img"
                        src={src}
                        alt={`${category.title} gallery ${i + 1}`}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          transition: "transform 0.4s ease",
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          width: "100%",
          background:
            "linear-gradient(145deg, #9e1569 0%, #c11a83 45%, #a8176e 100%)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1500px",
            margin: "0 auto",
            px: { xs: "20px", sm: "40px", lg: "80px" },
            py: { xs: "48px", md: "64px" },
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
            <Typography
              component="h2"
              sx={{
                color: "#fff",
                fontSize: { xs: "28px", md: "36px" },
                fontWeight: 800,
                letterSpacing: "-0.02em",
              }}
            >
              Explore Other Categories
            </Typography>
            <Box
              sx={{
                height: "4px",
                width: "80px",
                borderRadius: "999px",
                background:
                  "linear-gradient(90deg, #23aef4 0%, rgba(255,255,255,0.85) 100%)",
                margin: "14px auto 0",
              }}
            />
          </Box>

          <Grid container spacing={2.5}>
            {otherCategories.map((item) => {
              const itemAccent = typeAccent[item.type] || "#23aef4";
              return (
                <Grid item xs={12} sm={6} md={4} key={item.link}>
                  <Box
                    onClick={() => navigate(item.link)}
                    sx={{
                      position: "relative",
                      minHeight: { xs: "220px", md: "260px" },
                      borderRadius: "20px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: "1px solid rgba(255, 255, 255, 0.25)",
                      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
                      transition: "transform 0.35s ease",
                      "&:hover": { transform: "translateY(-6px)" },
                      "&:hover .other-cat-image": {
                        transform: "scale(1.06)",
                      },
                    }}
                  >
                    <Box
                      className="other-cat-image"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${item.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        transition: "transform 0.5s ease",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(10,14,24,0.1) 0%, rgba(10,14,24,0.85) 100%)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        height: "100%",
                        minHeight: { xs: "220px", md: "260px" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        p: 2.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: itemAccent,
                          mb: 0.5,
                        }}
                      >
                        {item.type}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: { xs: "22px", md: "26px" },
                          fontWeight: 800,
                          lineHeight: 1.15,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          fontSize: "14px",
                          mt: 0.5,
                          mb: 1.5,
                        }}
                      >
                        Ages {item.age}
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#fff",
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        View category
                        <ArrowForwardIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default CategoryDetailTemplate;
